// src/pages/Admin/Talleres/HistorialCambios/index.jsx
import React, { useEffect, useState } from "react";
import {
  Card, Timeline, Tag, Button, Space, Typography,
  Alert, Empty, Tooltip, Modal, Spin, Tabs
} from "antd";
import {
  HistoryOutlined, UserOutlined, CalendarOutlined, RollbackOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined,
  FileTextOutlined, InfoCircleOutlined, ClockCircleOutlined,
  EditOutlined, DeleteOutlined, SyncOutlined, EyeOutlined
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import Talleres from "../../../../../service/Talleres";
import { useWithNotify, useNotify } from "../../../../../providers/NotificationProvider";
import { App } from "antd";
import useAuthContext from "../../../../../hooks/useAuthContext";
import { puedeAprobar, requiereAprobacion } from "../../../../../config/rbac";
import { FiClock, FiDatabase, FiEdit2 } from "react-icons/fi";
import { claseBtnPrimario, claseBtnSecundario } from "../../../../Ui/Admin/PiezasAdmin";
import { CabeceraModal, ChipCabecera, Diff, Etiqueta, etiquetaCampo, textoValor, claseTituloSeccion } from "../piezasTalleres";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Title, Text } = Typography;

// Qué pasó en cada versión, en palabras de la administración.
const ACCIONES = {
  creacion: 'Creación',
  modificacion: 'Modificación',
  aprobacion: 'Cambio aprobado',
  rechazo: 'Cambio rechazado',
  restauracion: 'Restauración',
  eliminacion: 'Eliminación',
  cambio_estado: 'Cambio de estado',
};

// Valor de un campo para la comparación del detalle.
const valorLegible = (campo, valor) => {
  if (['inscripcionInicio', 'inscripcionFin', 'actividadesInicio', 'actividadesFin'].includes(campo)) {
    return valor ? dayjs(valor).format('DD/MM/YYYY HH:mm') : 'Sin fecha';
  }
  if (valor !== null && typeof valor === 'object') return <span className="font-normal text-gray-500">Ver «Datos completos»</span>;
  return textoValor(valor);
};

export default function HistorialCambios() {
  const { tallerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [taller, setTaller] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  
  const withNotify = useWithNotify();
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  // Igual que la ficha de usuario: en pantallas angostas el detalle ocupa todo el ancho.
  const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

  // Restaurar una versión es aplicar un cambio sin pasar por la cola, así que lo
  // hace quien no necesita aprobación. Es la misma condición que exige el endpoint.
  const canRestore = puedeAprobar(user, "talleres");
  const isJefeRecinto = requiereAprobacion(user, "talleres");

  // Cargar taller e historial
  useEffect(() => {
    loadData();
  }, [tallerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar taller actual
      const tallerRes = await Talleres.getById(tallerId);
      setTaller(tallerRes);
      
      // Cargar historial
      const historialRes = await Talleres.ObtenerHistorial(tallerId);

      setHistorial(historialRes || []);
      
    } catch (error) {
      notify.error(error.message || "No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  };

  // Ver detalle de versión
  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setCompareModalVisible(true);
  };

  // Restaurar versión
  const handleRestoreVersion = (version) => {
    // Validar permisos
    if (!canRestore) {
      notify.warning("Solo administradores y coordinadores pueden restaurar versiones");
      return;
    }

    modal.confirm({
      title: "Confirmar restauración",
      icon: <RollbackOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>¿Estás seguro de restaurar el taller a la versión {version.version}?</p>
          <Alert
            message="Importante"
            description={
              <div>
                <p>• Se creará una nueva versión con los datos de la versión {version.version}</p>
                <p>• La versión actual ({taller?.version}) quedará en el historial</p>
                <p>• Esta acción es reversible</p>
              </div>
            }
            type="warning"
            showIcon
            className="mt-3"
          />
        </div>
      ),
      okText: "Restaurar",
      okType: "primary",
      cancelText: "Cancelar",
      width: 600,
      onOk: async () => {
        try {
          await withNotify(
            Talleres.RestaurarVersion(tallerId, version._id),
            {
              loading: "Restaurando versión...",
              success: "Versión restaurada exitosamente",
              error: "No se pudo restaurar la versión"
            }
          );
          loadData();
        } catch (error) {
          console.error('Error restaurando versión:', error);
        }
      }
    });
  };

  // Obtener icono según tipo de acción
  const getActionIcon = (tipoAccion) => {
    const icons = {
      'creacion': <CheckCircleOutlined style={{ color: '#0047bb' }} />,
      'modificacion': <EditOutlined style={{ color: '#0047bb' }} />,
      'aprobacion': <CheckCircleOutlined style={{ color: '#0047bb' }} />,
      'rechazo': <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      'restauracion': <RollbackOutlined style={{ color: '#faad14' }} />,
      'eliminacion': <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      'cambio_estado': <SyncOutlined style={{ color: '#722ed1' }} />
    };
    return icons[tipoAccion] || <FileTextOutlined />;
  };

  // Obtener color según tipo de acción
  const getActionColor = (tipoAccion) => {
    const colors = {
      'creacion': 'green',
      'modificacion': 'blue',
      'aprobacion': 'green',
      'rechazo': 'red',
      'restauracion': 'orange',
      'eliminacion': 'red',
      'cambio_estado': 'purple'
    };
    return colors[tipoAccion] || 'default';
  };

  // Renderizar diff de cambios
  const renderCambioDiff = (campo, valorAnterior, valorNuevo) => {
    // Formatear fechas (solo inscripción ahora)
    if (['inscripcionInicio', 'inscripcionFin'].includes(campo)) {
      return (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Text type="secondary">Anterior: </Text>
            <Text delete className="text-red-600">
              {valorAnterior ? dayjs(valorAnterior).format("DD/MM/YYYY HH:mm") : 'Sin fecha'}
            </Text>
          </div>
          <div>
            <Text type="secondary">Nuevo: </Text>
            <Text className="text-blue-600">
              {valorNuevo ? dayjs(valorNuevo).format("DD/MM/YYYY HH:mm") : 'Sin fecha'}
            </Text>
          </div>
        </div>
      );
    }

    // Labels más descriptivos para campos de fechas
    const campoLabels = {
      'inscripcionInicio': 'Apertura de inscripciones',
      'inscripcionFin': 'Cierre de inscripciones',
      'tipoDuracionSuscripcion': 'Tipo de duración de suscripción',
      'liberarCuposPorBaja': 'Liberar cupos automáticamente'
    };

    // Para valores booleanos
    if (typeof valorNuevo === 'boolean') {
      return (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Text type="secondary">Anterior: </Text>
            <Tag color={valorAnterior ? 'green' : 'red'}>
              {valorAnterior ? 'Sí' : 'No'}
            </Tag>
          </div>
          <div>
            <Text type="secondary">Nuevo: </Text>
            <Tag color={valorNuevo ? 'green' : 'red'}>
              {valorNuevo ? 'Sí' : 'No'}
            </Tag>
          </div>
        </div>
      );
    }

    // Para valores simples
    if (typeof valorNuevo !== 'object') {
      return (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Text type="secondary">Anterior: </Text>
            <Text delete className="text-red-600">
              {valorAnterior != null ? String(valorAnterior) : 'Sin valor'}
            </Text>
          </div>
          <div>
            <Text type="secondary">Nuevo: </Text>
            <Text className="text-blue-600">
              {valorNuevo != null ? String(valorNuevo) : 'Sin valor'}
            </Text>
          </div>
        </div>
      );
    }

    // Para objetos/arrays
    return (
      <div className="text-xs">
        <Text type="secondary">Ver en pestaña "Snapshot completo"</Text>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/admin/talleres/editar/${tallerId}`)}
          >
            Volver
          </Button>
          <div>
            <Title level={3} className="!mb-0">
              <HistoryOutlined className="mr-2" />
              Historial de Cambios
            </Title>
            {taller && (
              <Text type="secondary">
                {taller.nombre} - Versión actual: {taller.version}
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* Alerta para jefe de recinto */}
      {isJefeRecinto && (
        <Alert
          message="Modo solo lectura"
          description="Como Jefe de Recinto, puedes ver el historial de cambios pero no restaurar versiones anteriores. Solo Administradores y Coordinadores pueden realizar esta acción."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          closable
          className="mb-4"
        />
      )}

      {/* Alert informativo para admin/coordinador */}
      {canRestore && (
        <Alert
          message="Sistema de versionado"
          description="Cada cambio realizado al taller queda registrado. Puedes ver los detalles de cada versión y restaurar a versiones anteriores si es necesario."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          closable
          className="mb-4"
        />
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <Spin size="large" />
          <div className="mt-4">
            <Text type="secondary">Cargando historial...</Text>
          </div>
        </div>
      )}

      {/* Historial vacío */}
      {!loading && historial.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No hay historial de cambios registrado"
          />
        </Card>
      )}

      {/* Timeline de historial */}
      {!loading && historial.length > 0 && (
        <Card>
          <Timeline
            mode="left"
            items={historial.map((item, index) => ({
              dot: getActionIcon(item.tipoAccion),
              color: getActionColor(item.tipoAccion),
              children: (
                <Card
                  size="small"
                  className={`mb-3 ${index === 0 ? 'border-2 border-blue-400' : ''}`}
                  title={
                    <div className="flex items-center justify-between">
                      <Space>
                        <Tag color={getActionColor(item.tipoAccion)}>
                          Versión {item.version}
                        </Tag>
                        <Text strong className="capitalize">
                          {item.tipoAccion.replace('_', ' ')}
                        </Text>
                        {index === 0 && (
                          <Tag color="blue">Versión actual</Tag>
                        )}
                      </Space>
                      <Space>
                        <Tooltip title="Ver detalles">
                          <Button
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => handleViewVersion(item)}
                          >
                            Ver
                          </Button>
                        </Tooltip>
                        {/* Botón restaurar solo para admin/coordinador */}
                        {index !== 0 && canRestore && (
                          <Tooltip title="Restaurar a esta versión">
                            <Button
                              icon={<RollbackOutlined />}
                              size="small"
                              type="primary"
                              onClick={() => handleRestoreVersion(item)}
                            >
                              Restaurar
                            </Button>
                          </Tooltip>
                        )}
                        {/* Tooltip informativo para jefe de recinto */}
                        {index !== 0 && !canRestore && (
                          <Tooltip title="Solo administradores y coordinadores pueden restaurar versiones">
                            <Button
                              icon={<RollbackOutlined />}
                              size="small"
                              disabled
                            >
                              Restaurar
                            </Button>
                          </Tooltip>
                        )}
                      </Space>
                    </div>
                  }
                >
                  <div className="space-y-2">
                    {/* Información del usuario */}
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <Space>
                        <UserOutlined className="text-gray-400" />
                        <Text>{item.realizadoPor?.nombre || 'Sistema'}</Text>
                        <Tag size="small" color="purple">
                          {item.realizadoPor?.rol || 'sistema'}
                        </Tag>
                      </Space>
                      <Space>
                        <CalendarOutlined className="text-gray-400" />
                        <Text type="secondary">
                          {dayjs(item.fechaRegistro).format("DD/MM/YYYY HH:mm")}
                        </Text>
                        <Text type="secondary" className="text-xs">
                          ({dayjs(item.fechaRegistro).fromNow()})
                        </Text>
                      </Space>
                    </div>

                    {/* Descripción del cambio */}
                    <div className="bg-gray-50 p-3 rounded">
                      <Text className="text-sm">
                        {item.descripcionCambio || 'Sin descripción'}
                      </Text>
                    </div>

                    {/* Campos modificados */}
                    {item.camposModificados && item.camposModificados.length > 0 && (
                      <div className="mt-2">
                        <Text strong className="text-xs text-gray-600">
                          Campos modificados ({item.camposModificados.length}):
                        </Text>
                        <div className="mt-2 space-y-2">
                          {item.camposModificados.slice(0, 3).map((cambio, idx) => (
                            <div key={idx} className="bg-white p-2 rounded border">
                              <Text strong className="text-sm capitalize block mb-1">
                                {cambio.campo === 'inscripcionInicio' ? 'Apertura de inscripciones' :
                                 cambio.campo === 'inscripcionFin' ? 'Cierre de inscripciones' :
                                 cambio.campo.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                              </Text>
                              {renderCambioDiff(
                                cambio.campo,
                                cambio.valorAnterior,
                                cambio.valorNuevo
                              )}
                            </div>
                          ))}
                          {item.camposModificados.length > 3 && (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleViewVersion(item)}
                            >
                              Ver {item.camposModificados.length - 3} cambios más...
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ),
            }))}
          />
        </Card>
      )}

      {/* Modal de detalle de versión */}
      <Modal
        open={compareModalVisible}
        onCancel={() => setCompareModalVisible(false)}
        footer={null}
        closable={false}
        title={null}
        width={angosto ? '100%' : 900}
        destroyOnHidden
        styles={{ content: { padding: 0, overflow: 'hidden', borderRadius: 24 }, body: { padding: 0, background: '#f4f6f9' } }}
      >
        {selectedVersion && (() => {
          const esActual = selectedVersion.version === taller?.version;
          const campos = selectedVersion.camposModificados || [];
          return (
            <>
              <CabeceraModal
                icon={<FiClock size={24} />}
                titulo={`Versión ${selectedVersion.version}`}
                subtitulo={taller?.nombre}
                onClose={() => setCompareModalVisible(false)}
                chips={(
                  <>
                    <ChipCabecera>{ACCIONES[selectedVersion.tipoAccion] || selectedVersion.tipoAccion}</ChipCabecera>
                    {esActual && <ChipCabecera>Versión actual</ChipCabecera>}
                  </>
                )}
                datos={[
                  {
                    label: 'Lo hizo',
                    value: selectedVersion.realizadoPor?.nombre || 'Sistema',
                    sub: selectedVersion.realizadoPor?.rol,
                  },
                  {
                    label: 'Fecha',
                    value: dayjs(selectedVersion.fechaRegistro).fromNow(),
                    sub: dayjs(selectedVersion.fechaRegistro).format('DD/MM/YYYY HH:mm'),
                  },
                ]}
              />

              <div className="p-5 sm:p-6 space-y-4">
                {selectedVersion.descripcionCambio && (
                  <div className="p-4 rounded-2xl bg-white border border-gray-100">
                    <p className={claseTituloSeccion}>Qué se hizo</p>
                    <p className="text-sm text-gray-900 m-0 mt-1">{selectedVersion.descripcionCambio}</p>
                  </div>
                )}

                <section className="rounded-3xl bg-white border border-gray-100 px-5 pb-5">
                  <Tabs items={[
                    {
                      key: 'cambios',
                      label: <Etiqueta icon={<FiEdit2 size={15} />} texto="Qué cambió" n={campos.length} />,
                      children: campos.length === 0 ? (
                        <p className="text-sm text-gray-400 py-6 text-center m-0">Esta versión no registra campos modificados.</p>
                      ) : (
                        <div className="space-y-3">
                          {campos.map((c, idx) => (
                            <Diff
                              key={idx}
                              titulo={etiquetaCampo(c.campo)}
                              etiquetas={['Antes', 'Después']}
                              antes={valorLegible(c.campo, c.valorAnterior)}
                              propuesto={valorLegible(c.campo, c.valorNuevo)}
                            />
                          ))}
                        </div>
                      ),
                    },
                    {
                      key: 'snapshot',
                      label: <Etiqueta icon={<FiDatabase size={15} />} texto="Datos completos" />,
                      children: (
                        <>
                          <p className="text-xs text-gray-500 mt-0 mb-2">Así estaba guardado el taller en esta versión. Es lo que se recupera al restaurarla.</p>
                          <pre className="text-xs overflow-auto max-h-96 m-0 p-3 rounded-xl bg-gray-50 border border-gray-100">
                            {JSON.stringify(selectedVersion.snapshot, null, 2)}
                          </pre>
                        </>
                      ),
                    },
                  ]} />
                </section>

                {!esActual && (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {!canRestore && (
                      <span className="text-xs text-gray-500 mr-auto">Solo administración y coordinación pueden restaurar versiones.</span>
                    )}
                    <button type="button" onClick={() => setCompareModalVisible(false)} className={claseBtnSecundario}>Volver</button>
                    <button
                      type="button"
                      disabled={!canRestore}
                      onClick={() => { setCompareModalVisible(false); handleRestoreVersion(selectedVersion); }}
                      className={claseBtnPrimario}
                    >
                      <RollbackOutlined /> Volver a esta versión
                    </button>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}
